import type {
  Analysis,
  ReturnsFactorAnalysis,
  PortfolioContributionPerformanceSummary,
  FactorExposureComponents,
  PortfolioContributionFactorExposureNode,
  CustomizedBlock,
  NotablePeriod,
  FrequencyEnum,
  ReturnsHistogram,
  Scenario,
} from 'venn-api';
import { isNil, get, compact, pick, sortBy } from 'lodash';
import type { CategoryMetric, CustomNotablePeriod, CustomizableMetric } from 'venn-utils';
import {
  Dates,
  getAllPredefinedPeriods,
  getPeriodLengthWithFrequency,
  isMetricAlwaysInPercentFormat,
  Numbers,
  RESIDUAL_FACTOR_ID,
  RISK_FREE_RATE_FACTOR_ID,
  TOTAL_FACTOR_ID,
} from 'venn-utils';
import type {
  MetricRowData,
  PortfolioRowData,
  ValueMapper,
  FactorRowData,
  DataValue,
  PortfolioBreakdownRowData,
  MultiGlobalColumnsRowData,
  ColumnData,
  ScenarioRowData,
  ScenarioColumnData,
} from '../types';
import { TOTAL_LABEL_CUMULATIVE, TOTAL_LABEL, FACTOR_CONTRIBUTION_ANALYSIS_TYPES } from '../customAnalysisContants';
import type { NotablePeriodMetadata, NotablePeriodMetadataUnionType } from './notablePeriodsUtils';
import {
  getCustomPeriodAnalyses,
  getPredefinedPeriodAnalyses,
  getRecoveryMonths,
  NotablePeriodMetadataPropertySet,
  ratioToPercentageChange,
} from './notablePeriodsUtils';
import { getExcelFormattedScenarioShock, getYAxisLabel } from '../components/charts/returns-distribution/utils';
import type { StudioRequestSubject } from 'venn-state';
import { convertByUnits, getUnitPrecision, getUnitSymbol } from '../../utils/scenario';

export const getRelativeValue = (relativeToTotal?: boolean, totalValue?: number, value?: number) => {
  if (isNil(value) || totalValue === 0 || !relativeToTotal) {
    return value;
  }

  if (isNil(totalValue)) {
    return undefined;
  }

  return Number(value) / totalValue;
};

export const portfolioStrategyBreakdownParser =
  (subjects: StudioRequestSubject[]) => (): PortfolioRowData<PortfolioBreakdownRowData>[] => {
    if (subjects.length > 0) {
      const subject = subjects[0];
      if (subject.subjectType === 'PORTFOLIO' && !subject.private) {
        const portfolio = subject.strategy ?? subject.portfolio;
        if (!portfolio.children?.length) {
          return [
            {
              label: portfolio.name,
              key: portfolio.name,
              allocation: [portfolio.allocation],
              isStrategy: isNil(portfolio.fund),
              path: [],
              value: [{ weight: 1 }],
            },
          ];
        }
        return portfolio.children.map((c) => {
          return {
            label: c.name,
            key: c.name,
            allocation: [c.allocation],
            isStrategy: isNil(c.fund),
            path: [c.name],
            value: [{ weight: c.allocation && portfolio.allocation ? c.allocation / portfolio.allocation : 0 }],
          };
        });
      }
      return [
        {
          label: subject.name,
          key: subject.name,
          allocation: [1],
          isStrategy: false,
          path: [subject.name],
          value: [
            {
              weight: 1,
            },
          ],
        },
      ];
    }
    return [];
  };

export const scenarioParser =
  (scenarios: Scenario[]) =>
  (
    selectedBlock: CustomizedBlock,
    analysesGroup?: (Analysis | undefined)[][],
  ): ScenarioColumnData[] | ScenarioRowData[] => {
    const firstSuccessfulScenario = analysesGroup?.find((s) => s?.[0]?.scenarios?.length);

    if (selectedBlock.infoGraphicType === 'DISTRIBUTE_BAR') {
      if (!analysesGroup) {
        return [];
      }

      return analysesGroup.flatMap((group) =>
        group[0]
          ? ({
              xAxisCategories:
                firstSuccessfulScenario && firstSuccessfulScenario[0]?.scenarios
                  ? firstSuccessfulScenario[0]?.scenarios
                      .find((s) => !!s)
                      ?.map(
                        (groupScenario, index) =>
                          `${scenarios[index].fundName}:\n${scenarios[index].shock > 0 ? '+' : ''}${parseFloat(
                            convertByUnits(scenarios[index].shock, scenarios[index].units),
                          ).toFixed(getUnitPrecision(scenarios[index].units))} ${getUnitSymbol(
                            scenarios[index].units,
                          )}`,
                      )
                  : [],
              yAxisTitle: `Estimated${selectedBlock.relativeToBenchmark ? ' Relative' : ''} Return`,
              percent: true,
              seriesData: group[0]?.scenarios
                ? group[0]?.scenarios
                    .find((s) => !!s)
                    ?.map((groupScenario) =>
                      !isNil(groupScenario.predicted)
                        ? Number.parseFloat(Numbers.safeFormatNumber(groupScenario.predicted * 100, 2))
                        : '--',
                    )
                : [],
              exportable: true,
              relative: selectedBlock.relativeToBenchmark,
              scenarioAnalyses: group?.length && group[0]?.scenarios?.length ? group[0].scenarios[0] : undefined,
              scenarios,
            } as ScenarioColumnData)
          : [],
      );
    }

    const rows =
      analysesGroup && firstSuccessfulScenario
        ? firstSuccessfulScenario
            .find((s) => s?.scenarios?.length)
            ?.scenarios[0]?.map((scenario, index) => {
              const scenarioAnalyses = analysesGroup.map((group) => {
                return group?.[0]?.scenarios?.[0]?.[index];
              });
              return {
                label: scenarios[index].fundName,
                scenarioShockXLS: getExcelFormattedScenarioShock(scenarios[index]),
                scenarioAnalysis: scenarioAnalyses,
                scenario: scenarios[index],
                relative: selectedBlock.relativeToBenchmark,
                hasError: scenarioAnalyses.some((analysis) => analysis?.status === 'FAILURE'),
              } as ScenarioRowData;
            })
        : [];

    return rows || [];
  };

export const performanceSummaryParser =
  (metrics: CustomizableMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): MetricRowData[] => {
    const relative = selectedBlock.relativeToBenchmark;
    const rows = selectedBlock.selectedMetrics.map((key) => {
      const metric = metrics.find((m) => m.key === key);
      if (!metric) {
        return undefined;
      }

      const label = relative ? metric.relativeLabel ?? metric.label : metric.label;

      // Special case for periodReturn
      const analysis = analysesGroup?.[0]?.find((a) => a?.analysisType === metric.analysisType);
      const analysisResult = metric.analysisResultKey ? analysis?.[metric.analysisResultKey] : analysis;
      const cumulativeLabel =
        key === 'periodReturn' && analysisResult?.[0]?.periodAnnualized === false ? ' (Cumulative)' : '';

      return {
        key,
        label: `${label}${cumulativeLabel}`,
        value: (analysesGroup?.map((analyses) => {
          const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
          const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
          return path
            ? get(analysis?.[metric.analysisResultKey!]?.[0], path)
            : analysis?.[metric.analysisResultKey!]?.[0];
        }) ?? []) as DataValue[],
        type: metric.dataType,
      };
    });

    return compact(rows);
  };

type DataMapper = { [key: string]: PortfolioRowData<ValueMapper> };

export const portfolioContributionParser =
  (metrics: CustomizableMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): PortfolioRowData<ValueMapper>[] => {
    const rows: PortfolioRowData<ValueMapper>[] = [];

    const totalLabel =
      analysesGroup?.[0]?.[0]?.historicalPerformancePortfolioContributions?.[0]?.periodAnnualized === false
        ? TOTAL_LABEL_CUMULATIVE
        : TOTAL_LABEL;
    const totalRow: PortfolioRowData<ValueMapper> = {
      label: totalLabel,
      path: [totalLabel],
      value: [],
      isStrategy: true,
      allocation: [],
    };
    const mapper: DataMapper = {};
    const relative = selectedBlock.relativeToBenchmark;
    const relativeToTotal = selectedBlock.contributionToPercentage;

    analysesGroup?.forEach((analyses, index) => {
      parsePortfolioContributionRows(metrics, analyses, relative, totalRow, index, relativeToTotal, mapper);
      parsePortfolioFactorContributionRows(analyses, metrics, relative, totalRow, index, mapper, relativeToTotal);
    });
    rows.push(totalRow);
    rows.push(...Object.values(mapper));
    return rows;
  };

export const returnsDistributionParser =
  (metrics: CustomizableMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): ColumnData[] => {
    const analysis = analysesGroup?.[0]?.[0];
    const metric = metrics?.[0];
    if (isNil(analysis) || isNil(metric)) {
      return [];
    }

    const analysisResults = get(analysis, metric.analysisResultKey!) as ReturnsHistogram[];
    const seriesData = analysisResults.map<(number | null)[]>((returnsHistogram) =>
      (returnsHistogram?.returns || []).map(({ count }) => count),
    );
    const yAxisLabel = getYAxisLabel(analysisResults?.[0]?.frequency ?? 'MONTHLY');
    const xAxisCategories = analysisResults?.[0]?.returns?.map(({ name }: { name: string }) => name);

    return seriesData.map((serieData, index) => ({
      seriesData: serieData,
      xAxisCategories,
      yAxisLabel,
      exportable: analysis.exportable[index],
    }));
  };

export const notablePeriodsParser =
  (
    analysisFrequency: FrequencyEnum,
    defaultSelectedPeriodIds?: number[],
    customNotablePeriods?: CustomNotablePeriod[],
  ) =>
  (
    selectedBlock: CustomizedBlock,
    analysesGroup?: (Analysis | undefined)[][],
  ): MultiGlobalColumnsRowData[] | ColumnData[] => {
    if (isNil(analysesGroup) || !analysesGroup?.length) {
      return [];
    }

    const allPeriods = getAllPredefinedPeriods(analysesGroup);
    const selectedNotablePeriodIds = isNil(defaultSelectedPeriodIds)
      ? allPeriods.map((period) => period.id)
      : defaultSelectedPeriodIds;

    const periodById: { [key: number]: NotablePeriod } = {};
    allPeriods.forEach((period) => {
      periodById[period.id] = period;
    });

    const predefinedPeriodMetadata: NotablePeriodMetadata[] = selectedNotablePeriodIds
      .filter((notablePeriodId) => !isNil(periodById[notablePeriodId]))
      .map((notablePeriodId) =>
        pick<NotablePeriod, NotablePeriodMetadataUnionType>(
          periodById[notablePeriodId],
          NotablePeriodMetadataPropertySet,
        ),
      );

    const customPeriodMetadata: NotablePeriodMetadata[] =
      customNotablePeriods
        ?.filter((period) => selectedNotablePeriodIds.find((id) => id === period.id))
        ?.map(({ name, start, end, id }) => ({
          id,
          name,
          start,
          extremumDate: end,
        })) ?? [];

    const analyses: (NotablePeriod | undefined)[][] = [
      ...getPredefinedPeriodAnalyses(predefinedPeriodMetadata, analysesGroup),
      ...getCustomPeriodAnalyses(customNotablePeriods, customPeriodMetadata, analysesGroup),
    ];
    const allMetadata = [...predefinedPeriodMetadata, ...customPeriodMetadata];

    if (selectedBlock.infoGraphicType === 'DISTRIBUTE_BAR') {
      const xAxisCategories = sortBy(allMetadata, ({ id }) => selectedNotablePeriodIds.indexOf(id)).map(
        ({ name, start, extremumDate }) => `${name} (${Dates.toDDMMMYYYY(start)} - ${Dates.toDDMMMYYYY(extremumDate)})`,
      );

      const resultsGroupedBySubject: ColumnData[] = [];
      for (let j = 0; j < analyses?.[0]?.length; j++) {
        const seriesData: { value: number | null; periodId: number }[] = [];
        for (let i = 0; i < analyses.length; i++) {
          const value = ratioToPercentageChange(analyses[i][j]?.extremumValue);
          seriesData.push({
            value: !isNil(value) ? Number.parseFloat(Numbers.safeFormatNumber(value, 2)) : null,
            periodId: allMetadata[i].id,
          });
        }
        resultsGroupedBySubject.push({
          xAxisCategories,
          yAxisTitle: 'Return',
          percent: true,
          seriesData: sortBy(seriesData, ({ periodId }) => selectedNotablePeriodIds.indexOf(periodId)).map(
            ({ value }) => value,
          ),
          exportable: true,
        });
      }

      return resultsGroupedBySubject;
    }

    return sortBy(
      allMetadata.map((notablePeriodMetadata: NotablePeriodMetadata, periodRowIdx: number) => ({
        key: `${notablePeriodMetadata.id}`, // keys need to be strings
        label: notablePeriodMetadata.name,
        globalValue: {
          start: Dates.toDDMMMYYYY(notablePeriodMetadata.start),
          end: Dates.toDDMMMYYYY(notablePeriodMetadata.extremumDate),
          period: isNil(notablePeriodMetadata.extremumDate)
            ? '--'
            : getPeriodLengthWithFrequency(
                analysisFrequency,
                notablePeriodMetadata.start,
                notablePeriodMetadata.extremumDate,
              ),
        },
        value: analyses[periodRowIdx].map((currentPeriod: NotablePeriod | undefined) => {
          if (isNil(currentPeriod)) {
            return {
              recovery: undefined,
              amount: undefined,
            };
          }
          const amount = ratioToPercentageChange(currentPeriod.extremumValue);
          const recovery = currentPeriod.end;
          const end = currentPeriod.extremumDate;
          return {
            recovery:
              !isNil(recovery) && !isNil(amount) && amount < 0
                ? getRecoveryMonths(end, recovery, analysisFrequency)
                : undefined,
            amount: isNil(amount) ? undefined : amount / 100,
          };
        }),
      })),
      ({ key }) => selectedNotablePeriodIds.indexOf(Number.parseInt(key, 10)),
    );
  };

const SEPERATOR = '-';
const fillTreeRowData = (
  summary: PortfolioContributionPerformanceSummary,
  index: number,
  parentPath: string[],
  mapper: DataMapper,
  isRoot: boolean,
  relativeToTotal: boolean,
  totalAllocation: number,
  totalValue: number | undefined,
  key: string,
  valuePath: string | undefined,
  usedNamesMap: { [name: string]: number },
) => {
  const currentNameIdx = usedNamesMap[summary.name] ?? 0;
  usedNamesMap[summary.name] = currentNameIdx + 1;
  const currentName = `${summary.name}(${currentNameIdx})`;

  if (!isRoot) {
    const currentPath = [...parentPath, currentName];
    const path = currentPath.join(SEPERATOR);
    if (!mapper[path]) {
      mapper[path] = {
        label: summary.name,
        path: currentPath,
        value: [],
        allocation: [],
        isStrategy: !summary.fundId,
      };
    }

    if (key === 'allocation') {
      mapper[path].allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, summary.allocation);
    } else if (key === 'allocation-VALUE' || key === 'allocation-PERCENT') {
      const actualRelativeToTotal = isMetricAlwaysInPercentFormat(key);
      const parsedValue = getRelativeValue(actualRelativeToTotal, totalAllocation, summary.allocation);
      mapper[path].value[index] = mapper[path].value[index] ?? {};
      mapper[path].value[index][key] = parsedValue;
    } else {
      const parsedValue = getRelativeValue(
        relativeToTotal,
        totalValue,
        valuePath ? get(summary.value, valuePath) : summary.value,
      );
      mapper[path].value[index] = mapper[path].value[index] ?? {};
      mapper[path].value[index][key] = parsedValue;
    }
  }

  if (summary?.children && summary.children.length > 0) {
    const childrenNamesMap = {};
    const newParentPath = isRoot ? [] : [...parentPath, currentName];
    summary.children.forEach((p) =>
      fillTreeRowData(
        p,
        index,
        newParentPath,
        mapper,
        false,
        relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        valuePath,
        childrenNamesMap,
      ),
    );
  }
};

const fillFactorTreeRowData = (
  summary: PortfolioContributionFactorExposureNode,
  index: number,
  parentPath: string[],
  mapper: DataMapper,
  isRoot: boolean,
  relativeToTotal: boolean,
  totalAllocation: number,
  totalValue: { [key: number]: number },
  key: string,
  factors: number[] | undefined,
  valueField: string | undefined,
  valuePath: string | undefined,
  usedNamesMap: { [key: string]: number },
) => {
  const currentNameIdx = usedNamesMap[summary.name] ?? 0;
  usedNamesMap[summary.name] = currentNameIdx + 1;
  const currentName = `${summary.name}(${currentNameIdx})`;

  if (!isRoot && factors) {
    for (const factor of factors) {
      const currentPath = [...parentPath, currentName];
      const path = currentPath.join(SEPERATOR);
      if (!mapper[path]) {
        mapper[path] = {
          label: summary.name,
          path: currentPath,
          value: [],
          allocation: [],
          isStrategy: !summary.fundId,
        };
      }

      if (key === 'allocation') {
        mapper[path].allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, summary.allocation);
      } else {
        const parsedValue = getRelativeValue(
          relativeToTotal,
          totalValue[factor] ?? 0.0,
          valuePath ? get(summary.value, `${valueField}.${factor}.${valuePath}`) : summary.value,
        );

        mapper[path].value[index] = mapper[path].value[index] ?? {};
        mapper[path].value[index][`${key}.${factor}`] = parsedValue;
      }
    }
  }

  if (summary?.children && summary.children.length > 0) {
    const newParentPath = isRoot ? [] : [...parentPath, currentName];
    const childrenNamesMap = {};
    summary.children.forEach((p) =>
      fillFactorTreeRowData(
        p,
        index,
        newParentPath,
        mapper,
        false,
        relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        factors,
        valueField,
        valuePath,
        childrenNamesMap,
      ),
    );
  }
};

export const factorsParser =
  (metrics: CustomizableMetric[], factors?: CategoryMetric[]) =>
  (selectedBlock: CustomizedBlock, analysesGroup?: (Analysis | undefined)[][]): FactorRowData[] => {
    const relativeToTotal = selectedBlock.contributionToPercentage;
    const totalValues: ValueMapper[] = (analysesGroup ?? []).map((analyses) => {
      const totalValue = {};
      metrics.forEach((metric) => {
        const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
        if (analysis) {
          if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RISK') {
            totalValue[metric.key] = analysis[metric.analysisResultKey!]?.[0]?.annualizedTotalRisk;
          } else if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RETURN') {
            totalValue[metric.key] = analysis[metric.analysisResultKey!]?.[0]?.periodTotalReturn;
          }
        }
      });
      return totalValue;
    });

    const selectedFactors = compact(
      selectedBlock.selectedFactors?.map((f) => factors?.find((factor) => f === factor.id)),
    );

    return selectedFactors.map((f) => ({
      key: f.id,
      label: f.name,
      value: getFactorValues(relativeToTotal, totalValues, f.id, metrics, analysesGroup),
    }));
  };

const getFactorValues = (
  relativeToTotal: boolean,
  totalValue: ValueMapper[],
  factorId: string,
  metrics: CustomizableMetric[],
  analysesGroup?: (Analysis | undefined)[][],
) => {
  const specialRows = [TOTAL_FACTOR_ID, RISK_FREE_RATE_FACTOR_ID, RESIDUAL_FACTOR_ID];
  const isSpecialRows = specialRows.includes(factorId);

  return (analysesGroup ?? []).map((analyses, index) => {
    const parsedValue = {};
    metrics.forEach((metric) => {
      const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
      if (analysis) {
        if (isSpecialRows) {
          if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RISK') {
            if (factorId === TOTAL_FACTOR_ID) {
              parsedValue[metric.key] = analysis.factorContributionToRisk?.[0]?.annualizedTotalRisk;
            } else if (factorId === RESIDUAL_FACTOR_ID) {
              parsedValue[metric.key] = analysis.factorContributionToRisk?.[0]?.residualMarginalRisk;
            }
          } else if (analysis.analysisType === 'FACTOR_CONTRIBUTION_TO_RETURN') {
            if (factorId === TOTAL_FACTOR_ID) {
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.periodTotalReturn;
            } else if (factorId === RESIDUAL_FACTOR_ID) {
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.periodResidualReturn;
            } else if (factorId === RISK_FREE_RATE_FACTOR_ID) {
              parsedValue[metric.key] = analysis.factorContributionToReturn?.[0]?.riskFreeReturn;
            }
          }
        } else {
          const contribution = (
            analysis[metric.analysisResultKey!]?.[0] as ReturnsFactorAnalysis | undefined
          )?.factors.find((factor) => String(factor.id) === factorId);
          parsedValue[metric.key] = contribution?.marginalContribution;
        }
        parsedValue[metric.key] = getRelativeValue(
          relativeToTotal,
          getOptionalNumber(totalValue?.[index]?.[metric.key]),
          parsedValue[metric.key],
        );
      }
    });
    return parsedValue;
  });
};

const parsePortfolioContributionRows = (
  metrics: CustomizableMetric[],
  analyses: (Analysis | undefined)[],
  relative: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  index: number,
  relativeToTotal: boolean | undefined,
  mapper: DataMapper,
) => {
  metrics.forEach((metric) => {
    if (FACTOR_CONTRIBUTION_ANALYSIS_TYPES.includes(metric.analysisType!)) {
      return;
    }

    const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
    const contribution = analysis?.[metric.analysisResultKey!]?.[0]?.portfolioContribution as
      | PortfolioContributionPerformanceSummary
      | undefined;
    if (contribution) {
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
      const totalValue = path ? get(contribution.value, path) : contribution;
      const totalAllocation = contribution.allocation;
      totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
      const parsedValue =
        metric.key === 'allocation-VALUE' || metric.key === 'allocation-PERCENT'
          ? getRelativeValue(isMetricAlwaysInPercentFormat(metric.key), totalAllocation, totalAllocation)
          : getRelativeValue(relativeToTotal, totalValue, totalValue);
      totalRow.value[index] = totalRow.value[index] ?? {};
      totalRow.value[index][metric.key] = parsedValue;

      fillTreeRowData(
        contribution,
        index,
        [],
        mapper,
        true,
        !!relativeToTotal,
        totalAllocation,
        totalValue,
        metric.key,
        path,
        {},
      );
    }
  });
};

const parsePortfolioFactorContributionRows = (
  analyses: (Analysis | undefined)[],
  metrics: CustomizableMetric[],
  relative: boolean | undefined,
  totalRow: PortfolioRowData<ValueMapper>,
  index: number,
  mapper: DataMapper,
  relativeToTotal: boolean | undefined,
) => {
  metrics.forEach((metric) => {
    if (!FACTOR_CONTRIBUTION_ANALYSIS_TYPES.includes(metric.analysisType!)) {
      return;
    }

    const analysis = analyses.find((a) => a?.analysisType === metric.analysisType);
    const contribution = analysis?.[metric.analysisResultKey!]?.[0] as FactorExposureComponents | undefined;
    const key = metric.key;
    if (contribution) {
      const factors = contribution.factors.map((factor) => factor.id);
      const field = 'factorContribution';
      const path = relative && metric.relativeMetricPath ? metric.relativeMetricPath : metric.metricPath;
      const totalValue: { [key: number]: number } = {};
      const totalAllocation = contribution.portfolio.allocation;
      totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
      for (const factor of factors) {
        const valuePath = `${field}.${factor}.${path}`;
        totalValue[factor] = valuePath ? get(contribution.portfolio.value, valuePath) : contribution.portfolio.value;
        totalRow.allocation[index] = getRelativeValue(relativeToTotal, totalAllocation, totalAllocation);
        const parsedValue = getRelativeValue(relativeToTotal, totalValue[factor], totalValue[factor]);
        totalRow.value[index] = totalRow.value[index] ?? {};
        totalRow.value[index][`${key}.${factor}`] = parsedValue;
      }

      fillFactorTreeRowData(
        contribution.portfolio,
        index,
        [],
        mapper,
        true,
        !!relativeToTotal,
        totalAllocation,
        totalValue,
        key,
        factors,
        field,
        path,
        {},
      );
    }
  });
};

function getOptionalNumber(value: string | number | undefined | null) {
  if (isNil(value)) {
    return undefined;
  }

  const numericValue = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
}
